import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { createNewSupportCallFetch, fetchAllDnsBySearch, fetchDnsOptions, fetchSupportOccurrenceOrigins, fetchSupportRegionals } from "../api/fetchNewSupportCall";
import { fetchSupportRegionals as fetchSupportRegionalsList } from "../api/tas";
import { FetchNewSupportCallProps, IPagedSelectOptionItens, IPagedSelectOption, IPagedSelectResponse, IGetIPagedSelectProps, IDnOptionItens, IGetAllDnsOptionsResponse, FormFields } from "../models/newSupportCall";
import type { GroupBase, OptionsOrGroups } from 'react-select';
import { fetchBudgetUserProfile } from "../api/fetchBudget";
import { VehicleSupport } from "../components/support/types";
import { fetchOpenOccurrencesByChassis, fetchSupportVehicle } from "../api/tas";
import { fetchOccurrence } from "../api/fetchOccurence";
import { Occurrence } from '../models/occurrence';

const useNewSupportCall = (
  formData: FormFields,
  updateField: <K extends keyof FormFields>(field: K, value: FormFields[K]) => void,
) => {
  const intl = useIntl();
  const [pageRegionals, setPageRegionals] = useState(0);
  const [pageOccurrenceOrigins, setPageOccurrenceOrigins] = useState(0);
  const [dnOptions, setDnOptions] = useState<IDnOptionItens[]>([]);
  const [regionalOptions, setRegionalOptions] = useState<{ id: string; label: string }[]>([]);
  const [role, setRole] = useState('');

  const [selectedRegional, setSelectedRegional] = useState<IPagedSelectOptionItens | null>(null);
  const [selectedOccurrenceOrigin, setSelectedOccurrenceOrigin] = useState<IPagedSelectOptionItens | null>(null);
  const [vehicle, setVehicle] = useState<VehicleSupport | null>(null);
  const [isLoadingVehicleFetch, setIsLoadingVehicleFetch] = useState(false);
  const vehicleRequestId = useRef(0);
  const [duplicateOpenOccurrences, setDuplicateOpenOccurrences] = useState<any[]>([]);
  const [isCheckingDuplicateChassis, setIsCheckingDuplicateChassis] = useState(false);

  const [occurrenceId, setOccurrenceId] = useState<string | undefined>(undefined);
  const [mainOccurrenceData, setMainOccurrenceData] = useState<Occurrence | undefined>(undefined);

  const fetchVehicle = async (chassis: string) => {
    const requestId = ++vehicleRequestId.current;
    setIsLoadingVehicleFetch(true);

    try {
      const res = await fetchSupportVehicle(chassis);
      if (requestId === vehicleRequestId.current) {
        setVehicle(res);
      }

    } catch (error) {
      if (requestId === vehicleRequestId.current) {
        setVehicle(null);
      }
    } finally {
      if (requestId === vehicleRequestId.current) {
        setIsLoadingVehicleFetch(false);
      }
    }
  }

  const resetVehicle = () => {
    vehicleRequestId.current += 1;
    setVehicle(null);
    setIsLoadingVehicleFetch(false);
  };

  const checkDuplicateChassis = async (chassis: string) => {
    if (chassis.length !== 17) {
      setDuplicateOpenOccurrences([]);
      return;
    }

    setIsCheckingDuplicateChassis(true);

    try {
      const openOccurrences = await fetchOpenOccurrencesByChassis(chassis);
      setDuplicateOpenOccurrences(openOccurrences);
    } catch (error) {
      setDuplicateOpenOccurrences([]);
    } finally {
      setIsCheckingDuplicateChassis(false);
    }
  }

  const createSupportCall = async (props: FetchNewSupportCallProps) => {
    const response = await createNewSupportCallFetch(props);

    return response;
  };


  const getSelectOptions = async (
    page: number,
    setPage: React.Dispatch<React.SetStateAction<number>>,
    fetchFunction: (props: IGetIPagedSelectProps) => Promise<IPagedSelectResponse | null>
  ): Promise<IPagedSelectOption> => {
    const pageValue = page + 1;
    const response = await fetchFunction({ page, size: 10 });

    if (!response) {
      console.error("Erro ao buscar regionais de suporte");
      return { options: [], hasMore: false };
    }

    const isLastPage = response?.last;

    const itens = response?.content.map(item => ({
      value: item.id,
      label: item.name,
    }));


    setPage(pageValue);
    return {
      options: itens,
      hasMore: !isLastPage,
    };
  };

  const getSupportRegionals = async (
    _search: string,
    _prevOptions: OptionsOrGroups<IPagedSelectOptionItens, GroupBase<IPagedSelectOptionItens>>
  ): Promise<IPagedSelectOption> => {
    return await getSelectOptions(pageRegionals, setPageRegionals, fetchSupportRegionals);
  }

  const getSupportOccurrenceOrigins = async (
    _search: string,
    _prevOptions: OptionsOrGroups<IPagedSelectOptionItens, GroupBase<IPagedSelectOptionItens>>
  ): Promise<IPagedSelectOption> => {
    return await getSelectOptions(pageOccurrenceOrigins, setPageOccurrenceOrigins, fetchSupportOccurrenceOrigins);
  }

  const getUserRole = async () => {
    const userProfileData = await fetchBudgetUserProfile();

    setRole(userProfileData || '')
  }

  const getAllDns = async (
    searchTerm: string,
    pageSize: number,
    page: number
  ): Promise<IGetAllDnsOptionsResponse[]> => {

    const response = await fetchAllDnsBySearch(searchTerm || 'dibracam')

    if (!response) return []

    const options = response?.map(option => {
      return {
        id: `DN: ${option.id} | ${option.fantasyName}`,
        label: intl.formatMessage({ id: "intl-msg:shared.newSupportCall.dnSearchOptionLabel" }, { dn: option.id, name: option.fantasyName, neighborhood: option.neighborhood, city: option.city, state: option.state })
      }
    })

    return options
  }

  useEffect(() => {
    const loadData = async () => {
      const response = await fetchDnsOptions()

      if (!response) return []

      const itens = response?.dns
        ?.map(item => ({
          value: `DN: ${item.dn} | ${item.dnName}`,
          label: intl.formatMessage({ id: "intl-msg:shared.newSupportCall.dnOptionLabel" }, { dn: item.dn, name: item.dnName, address: item.address, neighborhood: item.neighborhood, city: item.city, state: item.state })
        })) || []

      setDnOptions(itens)
    }

    const loadRegionals = async () => {
      const response = await fetchSupportRegionalsList();
      if (!response?.content) return;

      setRegionalOptions(response.content.map((item: { id: number; name: string }) => ({
        id: String(item.id),
        label: item.name,
      })));
    }

    loadData()
    loadRegionals()
    getUserRole()
  }, [])

  useEffect(() => {
    const loadOccurrenceData = async () => {
      try {
        const occurrenceResponse = await fetchOccurrence(occurrenceId || '', "TOWER");

        if (!formData.chassis) {
          updateField('chassis', occurrenceResponse?.vehicleChassis)
          await fetchVehicle(occurrenceResponse?.vehicleChassis || '');
        }
        if (!formData.licensePlate) updateField('licensePlate', occurrenceResponse?.vehicleLicensePlate)
        if (!formData.customer) updateField('customer', occurrenceResponse?.customer)
        if (!formData.odometer) updateField('odometer', occurrenceResponse?.vehicleOdometer ? String(occurrenceResponse?.vehicleOdometer) : '')
        if (!formData.hourMeter) updateField('hourMeter', occurrenceResponse?.vehicleHourmeter ? String(occurrenceResponse?.vehicleHourmeter) : '')
        if (!formData.phone) updateField('phone', occurrenceResponse?.dealershipPhone)

        setMainOccurrenceData(occurrenceResponse);

      } catch (error) {
        resetOccurrenceVehicleData();
      }
    };

    loadOccurrenceData();
  }, [occurrenceId]);

  const resetOccurrenceVehicleData = () => {
    setMainOccurrenceData(undefined);
    updateField('chassis', '')
    updateField('licensePlate', '')
    updateField('customer', '')
    updateField('odometer', '')
    updateField('hourMeter', '')
    updateField('phone', '')
  };

  const handleClearOccurrence = () => {
    setOccurrenceId(undefined);
    setMainOccurrenceData(undefined);
  };

  return {
    selectedRegional,
    selectedOccurrenceOrigin,
    dnOptions,
    regionalOptions,
    role,
    vehicle,
    isLoadingVehicleFetch,
    duplicateOpenOccurrences,
    isCheckingDuplicateChassis,
    occurrenceId,
    mainOccurrenceData,
    fetchVehicle,
    resetVehicle,
    checkDuplicateChassis,
    createSupportCall,
    getSupportRegionals,
    getAllDns,
    getSupportOccurrenceOrigins,
    setSelectedRegional,
    setSelectedOccurrenceOrigin,
    setOccurrenceId,
    handleClearOccurrence
  };
};

export default useNewSupportCall;